import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section and other areas.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    // Styles for dynamically injected <overflow-list> elements are async.
    // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
    await this.#waitForStyles();

    this.#initialize();
  }

  async #waitForStyles() {
    /** @type {HTMLLinkElement | null | undefined} */
    const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

    // Styles missing or already loaded.
    if (styles && !styles.sheet) {
      await new Promise((resolve) => {
        styles.addEventListener('load', resolve, { once: true });
        styles.addEventListener('error', resolve, { once: true });
      });
    }

    // The document's stylesheets matter just as much: measuring before the theme's CSS applies sizes
    // the items at almost nothing, so the list concludes that everything fits (a "+N" that never
    // appears, or appears with the wrong number) and freezes a far too small height. That is the
    // "it is wrong on the first load and fixes itself once you scroll" bug.
    const pending = /** @type {HTMLLinkElement[]} */ (
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).filter(
        (link) => link instanceof HTMLLinkElement && !link.sheet
      )
    );

    if (pending.length) {
      await Promise.all(
        pending.map(
          (link) =>
            new Promise((resolve) => {
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            })
        )
      );
    }
  }

  /**
   * Web fonts change how wide the items and their neighbours are, and they land after the first
   * measurement. One extra reflow once they have settled keeps the count honest without polling.
   */
  #refreshWhenFontsSettle() {
    document.fonts?.ready.then(() => {
      // Only refine a measurement that already happened: reflowing an element that has not been laid
      // out yet would classify every item as overflowing.
      if (this.isConnected && this.#hasReflowed) this.#reflowItems();
    });
  }

  /**
   * Initialize the element
   */
  #initialize() {
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */ (event) => {
        this.#reflowItems(0, event.detail.lastVisibleElement);
      }
    );

    // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
    // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
    // Instead, we ignore it for now and remove it later on the first reflow.
    const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
    const firstElement = elements[0];
    const lastElement = elements[elements.length - 1];

    // Observe the first and last elements to trigger a reflow when they are visible.
    // That way we can get their height from the IntersectionObserver for free (without reflows).
    if (firstElement) {
      this.#intersectionObserver.observe(firstElement);
    }
    if (lastElement && lastElement !== firstElement) {
      this.#intersectionObserver.observe(lastElement);
    }

    this.#refreshWhenFontsSettle();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : /** @param {FrameRequestCallback} callback */ (callback) =>
          requestAnimationFrame(() => setTimeout(callback, 0));
  }

  #scheduled = false;

  /** Whether a first measurement has happened, so later refinements know there is something to refine. */
  #hasReflowed = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (entry?.isIntersecting) {
      this.#intersectionObserver.disconnect();
      setTimeout(() => {
        // Remove the leftover <template> for dynamically injected <overflow-list> elements.
        this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
        this.#reflowItems(entry.boundingClientRect.height);
      }, 0);
    }
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = () => {
    if (this.#scheduled) return;

    this.#scheduled = true;

    requestAnimationFrame(() =>
      setTimeout(() => {
        this.#reflowItems();
        this.#scheduled = false;
      }, 0)
    );
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#unobserveChanges();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   *
   * Two passes on purpose. The first one measures with the "More" item hidden and answers a single
   * question: does anything overflow at all? Only when the answer is yes does the second pass lay
   * the "More" item out on the line and work out how many items still fit beside it. Measuring in
   * one pass with the "More" item always present — as this component used to — permanently
   * reserved its width, so a list whose items fitted exactly still pushed the last one into the
   * overflow and showed a "+1" for an item that had room for itself.
   *
   * @param {number} [listHeight] Initial height of the list
   * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (listHeight = 0, lastVisibleElement = null) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#unobserveChanges();

    // Reset all elements to the default slot so we can check which ones overflow.
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    const lastElement = elements[elements.length - 1];

    if (!lastElement) {
      this.#observeChanges();
      return;
    }

    // Held only while the items are allowed to wrap, so the list cannot grow to two lines under the
    // measurement. It is removed again at the end: a height captured on the very first frame — before
    // the theme's CSS has sized the items — used to stay on the element for the life of the page and
    // clip the row. Nothing is painted between here and there, so no jump is visible either way.
    if (listHeight > 0) {
      list.style.setProperty('height', `${listHeight}px`);
    }

    // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
    list.style.setProperty('flex-wrap', 'wrap');
    placeholder.hidden = true;

    // Putting lastVisibleElement (if provided) at the start of the list keeps it visible.
    lastVisibleElement?.style.setProperty('order', '-1');

    // Pass 1 — with nothing reserved for the "More" item.
    moreSlot.hidden = true;

    let overflowingElements = this.#findOverflowingElements(elements);
    const hasOverflow = overflowingElements.length > 0;

    // Pass 2 — the "More" item will be shown, so it has to fit on the line too. Laying it out
    // first tells us in one measurement which items still share the line with it.
    if (hasOverflow) {
      // Give the counter its (near) final width before measuring it: `--overflow-count` still held
      // the previous value, so the button used to be measured as "+0".
      this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

      moreSlot.hidden = false;
      moreSlot.style.setProperty('order', '-1');

      const overflowingWithMore = this.#findOverflowingElements(elements, moreSlot);
      if (overflowingWithMore.length > 0) overflowingElements = overflowingWithMore;

      moreSlot.style.removeProperty('order');
    }

    lastVisibleElement?.style.removeProperty('order');

    const overflowing = new Set(overflowingElements);
    const visibleElements = elements.filter((element) => !overflowing.has(element));
    const placeholderWidth = overflowingElements[0]?.getBoundingClientRect().width ?? 0;

    // Move the elements to the correct slot.
    for (const element of elements) {
      const targetSlot = overflowing.has(element) ? overflowSlot.name : defaultSlot.name;
      if (element.slot !== targetSlot) {
        element.slot = targetSlot;
      }
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

    // Adjust the "More" button visibility.
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space.
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
    list.style.setProperty('overflow', 'unset');

    // The measurement is over and the overflowing items have moved out, so the list is a single line
    // again. Both properties the measurement needed are handed back to the stylesheet: a height
    // captured on the very first frame used to stay on the element for the life of the page and clip
    // the row, and a leftover `flex-wrap: wrap` sent the (zero-height, non-zero-width) placeholder to
    // a phantom second line, which grew the row by a whole line gap the moment a "+N" appeared.
    list.style.removeProperty('height');
    list.style.removeProperty('flex-wrap');

    this.#hasReflowed = true;

    hasOverflow && this.#updateMinimumReached(visibleElements);

    this.#observeChanges();
  };

  /**
   * Returns the elements that wrapped onto a line below the first one.
   *
   * Compared against the topmost item rather than against the "More" slot: that slot is
   * `height: 100%` of the list, so items shorter than the line (swatch chips, for instance) sat
   * below its top and every single one of them counted as overflowing — the "+N and no chips"
   * half of the product card bug, which used to need a CSS workaround per call site.
   *
   * @param {Element[]} elements - The items to classify
   * @param {HTMLElement} [extraItem] - An item sharing the first line (the "More" slot)
   * @returns {Element[]}
   */
  #findOverflowingElements(elements, extraItem) {
    const rects = elements.map((element) => element.getBoundingClientRect());
    const extraRect = extraItem?.getBoundingClientRect();
    const measured = extraRect ? [...rects, extraRect] : rects;

    let firstLineTop = Infinity;
    let tallest = 0;

    for (const rect of measured) {
      if (rect.width === 0 && rect.height === 0) continue;
      if (rect.top < firstLineTop) firstLineTop = rect.top;
      if (rect.height > tallest) tallest = rect.height;
    }

    if (firstLineTop === Infinity) return [];

    // Items of different heights are centred on their line, so an item that did fit can still sit
    // slightly lower than the tallest one — at most by half of it. An item that actually wrapped is
    // a whole line lower, so half the tallest item separates the two cases cleanly.
    const tolerance = Math.max(2, tallest / 2);

    return elements.filter((_, index) => rects[index].top > firstLineTop + tolerance);
  }

  #observeChanges() {
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }

  #unobserveChanges() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);

  #intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
    // Extend the root margin to around one more viewport of a typical mobile screen.
    rootMargin: '640px 360px 640px 360px',
  });
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
